import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { airtableService } from '../services/airtable';

const StudentLogin = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    studentName: '',
    accessCode: searchParams.get('access') || '',
    clinicCode: searchParams.get('clinic') || '',
    yearLevel: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [airtableConfigured, setAirtableConfigured] = useState(false);

  useEffect(() => {
    const configured = airtableService.loadSavedConfig();
    setAirtableConfigured(configured);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!airtableConfigured) {
      setError('Airtable is not configured. Please contact Dr. Grant to set up the system.');
      return;
    }

    if (!formData.studentName.trim() || !formData.accessCode.trim() || !formData.clinicCode.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!formData.yearLevel) {
      setError('Please select your year level (D3 or D4)');
      return;
    }

    if (formData.accessCode.length !== 4 || !/^\d+$/.test(formData.accessCode)) {
      setError('Access code must be exactly 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const session = await airtableService.validateSession(
        formData.accessCode, 
        formData.clinicCode.toUpperCase()
      );

      if (!session) {
        setError('Invalid access code or clinic code. Please check with Dr. Grant.');
        setLoading(false);
        return;
      }

      const isAlreadyInQueue = await airtableService.checkStudentInQueue(
        session.fields['Session ID'], 
        formData.studentName.trim()
      );

      if (isAlreadyInQueue) {
        setError('You are already in the queue for this session.');
        setLoading(false);
        return;
      }

      const userData = {
        name: formData.studentName.trim(),
        accessCode: formData.accessCode,
        clinicCode: formData.clinicCode.toUpperCase(),
        sessionId: session.fields['Session ID'],
        yearLevel: formData.yearLevel
      };

      onLogin(userData, 'student');
    } catch (error) {
      console.error('Login error:', error);
      setError('Unable to connect to the system. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!airtableConfigured) {
    return (
      <div className="container">
        <div className="header">
          <h1>NotesNCodes</h1>
          <p>Dental Student Queue Management</p>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'var(--coral)', marginBottom: '16px' }}>System Not Configured</h2>
          <p style={{ marginBottom: '24px' }}>
            The system needs to be set up by Dr. Grant before students can log in.
          </p>
          <Link to="/dr-grant/login" className="btn btn-primary">
            Dr. Grant Setup
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>NotesNCodes</h1>
        <p>🎓 Student Login</p>
        <small style={{ color: 'var(--text-light)' }}>For students joining the queue</small>
      </div>
      
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="studentName">Initials or Nickname</label>
            <input
              type="text"
              id="studentName"
              name="studentName"
              value={formData.studentName}
              onChange={handleChange}
              placeholder="e.g. JD, Jazz, TM"
              disabled={loading}
            />
            <small style={{ color: 'var(--text-light)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              For privacy, please use initials or a nickname instead of your full name.
            </small>
          </div>
          
          <div className="input-group">
            <label htmlFor="accessCode">Access Code (4 digits)</label>
            <input
              type="text"
              id="accessCode"
              name="accessCode"
              value={formData.accessCode}
              onChange={handleChange}
              placeholder="Enter 4-digit code from Dr. Grant"
              maxLength="4"
              disabled={loading}
            />
          </div>
          
          <div className="input-group">
            <label htmlFor="clinicCode">Clinic Code</label>
            <input
              type="text"
              id="clinicCode"
              name="clinicCode"
              value={formData.clinicCode}
              onChange={handleChange}
              placeholder="Enter clinic code (e.g. CLINIC-A)"
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <label>Year Level</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {['D3', 'D4'].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, yearLevel: level });
                    setError('');
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    border: formData.yearLevel === level ? '3px solid var(--primary-blue)' : '2px solid #e1e8ed',
                    backgroundColor: formData.yearLevel === level ? 'var(--primary-blue)' : 'white',
                    color: formData.yearLevel === level ? 'white' : 'var(--text-dark)',
                    borderRadius: '8px',
                    cursor: loading ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.6 : 1
                  }}
                  disabled={loading}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ 
              color: 'var(--coral)', 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#ffe6e6', 
              borderRadius: '6px' 
            }}>
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Logging in...' : 'Student Login'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link to="/dr-grant/login" style={{ color: 'var(--text-light)' }}>
            Are you Dr. Grant?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
